import React, { useMemo } from "react";
import {
  USAGE_CATEGORY_LABELS,
  UsageGroupComparisonDTO,
} from "../../../api/admin/types";
import {
  adjustWorkflowTotal,
  visibleCategories,
} from "../shared/constants";
import { deltaBg, deltaColor, formatSignedInt } from "../shared/formatters";

interface UserHeatmapProps {
  groups: UsageGroupComparisonDTO[];
  agencyName?: string;
  periodALabel?: string;
  periodBLabel?: string;
  onUserClick?: (userId: string) => void;
  showEpic?: boolean;
}

const UserHeatmap: React.FC<UserHeatmapProps> = ({
  groups,
  agencyName,
  periodALabel,
  periodBLabel,
  onUserClick,
  showEpic = true,
}) => {
  const cats = visibleCategories(showEpic);
  // Show every seat the BE returns - active users first (by absolute change),
  // then dormant ones at the bottom in muted styling. Hiding 0/0 users used
  // to make low-engagement teams look smaller than they actually are.
  const visibleUsers = useMemo(() => {
    const ranked = [...groups];
    ranked.sort((a, b) => {
      const aActive = a.period_a.total > 0 || a.period_b.total > 0;
      const bActive = b.period_a.total > 0 || b.period_b.total > 0;
      if (aActive !== bActive) return aActive ? -1 : 1;
      return Math.abs(b.delta.total) - Math.abs(a.delta.total);
    });
    return ranked;
  }, [groups]);

  const magnitude = useMemo(() => {
    let m = 0;
    for (const g of visibleUsers) {
      for (const c of cats) {
        const v = Math.abs(g.delta.totals[c] ?? 0);
        if (v > m) m = v;
      }
    }
    return m;
  }, [visibleUsers, cats]);

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 20,
        width: "100%",
        maxWidth: 1200,
        boxSizing: "border-box",
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>
          {agencyName ?? "Agency"} — task change by category
        </div>
        {periodALabel && periodBLabel ? (
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            {periodALabel} → {periodBLabel}
          </div>
        ) : null}
      </div>

      {visibleUsers.length === 0 ? (
        <div style={{ color: "#6b7280", padding: 16 }}>
          No users with activity in either period.
        </div>
      ) : (
        <table
          style={{
            borderCollapse: "separate",
            borderSpacing: 0,
            width: "100%",
            fontSize: 13,
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  textAlign: "left",
                  padding: "8px 10px",
                  position: "sticky",
                  left: 0,
                  background: "#fff",
                  borderBottom: "1px solid #e5e7eb",
                  fontSize: 12,
                  color: "#6b7280",
                  fontWeight: 600,
                }}
              >
                User
              </th>
              {cats.map((c) => (
                <th
                  key={c}
                  style={{
                    textAlign: "center",
                    padding: "8px 6px",
                    borderBottom: "1px solid #e5e7eb",
                    fontSize: 12,
                    color: "#6b7280",
                    fontWeight: 600,
                  }}
                >
                  {USAGE_CATEGORY_LABELS[c]}
                </th>
              ))}
              <th
                style={{
                  textAlign: "center",
                  padding: "8px 10px",
                  borderBottom: "1px solid #e5e7eb",
                  fontSize: 12,
                  color: "#6b7280",
                  fontWeight: 600,
                }}
              >
                Total Δ
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleUsers.map((g) => {
              const dormant = g.period_a.total === 0 && g.period_b.total === 0;
              return (
                <tr key={g.id} style={{ opacity: dormant ? 0.5 : 1 }}>
                  <td
                    style={{
                      padding: "8px 10px",
                      position: "sticky",
                      left: 0,
                      background: "#fff",
                      borderBottom: "1px solid #f3f4f6",
                      cursor: onUserClick ? "pointer" : "default",
                    }}
                    onClick={() => onUserClick?.(g.id)}
                  >
                    <div style={{ fontWeight: 600 }}>{g.label}</div>
                    {g.email ? (
                      <div style={{ fontSize: 11, color: "#6b7280" }}>{g.email}</div>
                    ) : null}
                    {g.team_name ? (
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>{g.team_name}</div>
                    ) : null}
                    {g.role ? (
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>{g.role}</div>
                    ) : null}
                  </td>
                  {cats.map((c) => {
                    const v = g.delta.totals[c] ?? 0;
                    // `policies` is a companion metric (uploads), not a workflow.
                    // Dim its column so the eye groups it separately from the
                    // category cells that actually contribute to the total.
                    const isCompanion = c === "policies";
                    return (
                      <td
                        key={c}
                        style={{
                          padding: "10px 6px",
                          textAlign: "center",
                          background: isCompanion
                            ? "#fafafa"
                            : deltaBg(v, magnitude),
                          color: isCompanion ? "#9ca3af" : deltaColor(v),
                          fontWeight: isCompanion ? 500 : 600,
                          fontStyle: isCompanion ? "italic" : "normal",
                          borderBottom: "1px solid #f3f4f6",
                        }}
                      >
                        {v === 0 ? "0" : formatSignedInt(v)}
                      </td>
                    );
                  })}
                  {(() => {
                    // Total Δ matches the visible cells: when Epic is hidden,
                    // subtract its contribution from the displayed sum.
                    const adjDelta = adjustWorkflowTotal(g.delta.total, g.delta.totals, showEpic);
                    return (
                      <td
                        style={{
                          padding: "10px 6px",
                          textAlign: "center",
                          fontWeight: 700,
                          color: deltaColor(adjDelta),
                          borderBottom: "1px solid #f3f4f6",
                        }}
                      >
                        {adjDelta === 0 ? "0" : formatSignedInt(adjDelta)}
                      </td>
                    );
                  })()}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default UserHeatmap;
